import React from "react"
import {
    Page,
    Text,
    View,
    Document,
} from "@react-pdf/renderer"
import { useTranslation } from "react-i18next"

import { styles } from "./PDFStyles"
import HeaderPDF from "./PDFHeader"
import DataViewPDF from "./PDFDataView"

import dateService from "services/date"
import { isNotEmpty } from "utils/validation"
import { OWNER_PRIVATE_GENDER, OWNER_COMPANY_GENDER, getLabelFromBikeId, getBikeLabelByText } from "utils/constants"

const PDFRequestTemplate = ({
    children,
    title,
    bike = null,
    entity = null,
    operatorGender = null,
}) => {
    const { t } = useTranslation()
    const contents = t("pdf.main_top_content", { ns: "bike", returnObjects: true })

    const activatedBikeDate =
        bike?.bikeLastRegisteredBy?.create_date
            ? dateService.formatDate(bike?.bikeLastRegisteredBy?.create_date, "dd/MM/y")
            : dateService.formatDate(new Date(), "dd/MM/y")

    const entityName =
        bike?.bikeLastRegisteredBy?.social_reason
            ? bike?.bikeLastRegisteredBy?.social_reason
            : entity?.entity_data?.raison_sociale

    /** Some bikes have no bikeTypeId specified from database. So return directly the bikeType. In this case, any translation is possible. */
    const bikeTypeLabel = isNotEmpty(bike?.bikeTypeId) ? getLabelFromBikeId(bike?.bikeTypeId) : getBikeLabelByText(bike?.bikeType)

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <HeaderPDF bike={bike} operatorGender={operatorGender} />

                {isNotEmpty(title) &&
                    <View style={styles.sectionTitle}>
                        <Text style={styles.h1}>{title}</Text>
                    </View>
                }

                {isNotEmpty(bike) &&
                    <View style={styles.section}>
                        <View>
                            <Text style={[styles.h2]}>{t("pdf.pdf_identification_title", { ns: "bike" })}</Text>
                        </View>

                        <View>
                            <Text style={[styles.p]}>{t("pdf.heading", { ns: "bike" })}</Text>
                            <Text style={[styles.p]}>{t("pdf.intro", { ns: "bike", entityName: entityName })}
                            </Text>
                            <Text style={[styles.p]}>{t("pdf.intro_2", { ns: "bike", registeredDate: activatedBikeDate })}</Text>
                            {
                                contents.map((content, index) => {
                                    return <Text style={styles.p} key={index}> {content} </Text>
                                })
                            }
                        </View>

                        <View style={styles.grid}>
                            <View style={styles.sectionRight}>
                                <Text style={styles.h3}>{t("pdf.bike_info", { ns: "bike" })}</Text>
                                <DataViewPDF
                                    label={t("brand", { ns: "bike" })}
                                    value={bike?.bikeBrand}
                                />
                                <DataViewPDF
                                    label={t("model", { ns: "bike" })}
                                    value={bike?.bikeModel}
                                />
                                <DataViewPDF
                                    label={t("type", { ns: "bike" })}
                                    value={t(`${bikeTypeLabel}`)}
                                />
                                <DataViewPDF
                                    label={t("color", { ns: "bike" })}
                                    value={bike?.bikeColor}
                                />

                                {bike?.isVAE === "true" &&
                                    <DataViewPDF
                                        label={t("type_electric", { ns: "bike" })}
                                        value={"Oui"}
                                    />
                                }

                                {bike?.isVAE === "false" &&
                                    <DataViewPDF
                                        label={t("type_electric", { ns: "bike" })}
                                        value={"Non"}
                                    />
                                }

                                <DataViewPDF
                                    label={t("code", { ns: "bike" })}
                                    value={bike?.code}
                                />
                                {isNotEmpty(bike?.bikeSerial) &&
                                    <DataViewPDF
                                        label={t("serial", { ns: "bike" })}
                                        value={bike?.bikeSerial}
                                    />
                                }
                                {isNotEmpty(bike?.bikeEngineSerial) &&
                                    <DataViewPDF
                                        label={t("serial_engine", { ns: "bike" })}
                                        value={bike?.bikeEngineSerial}
                                    />
                                }
                                {isNotEmpty(bike?.bikeBatterySerial) &&
                                    <DataViewPDF
                                        label={t("serial_battery", { ns: "bike" })}
                                        value={bike?.bikeBatterySerial}
                                    />
                                }
                            </View>
                            <View style={styles.sectionLeft}>
                                <Text style={styles.h3}>{t("pdf.personal_info", { ns: "bike" })}</Text>
                                {bike?.owner?.ownerGender ?
                                    <>
                                        {bike?.owner?.ownerGender === OWNER_COMPANY_GENDER &&
                                            <DataViewPDF
                                                label={t("owner.raison_sociale", { ns: "bike" })}
                                                value={bike?.owner.ownerRaisonSociale}
                                            />
                                        }
                                        {bike?.owner?.ownerGender === OWNER_PRIVATE_GENDER &&
                                            <>
                                                <DataViewPDF
                                                    label={t("owner.name", { ns: "bike" })}
                                                    value={bike?.owner.ownerName}
                                                />
                                                <DataViewPDF
                                                    label={t("owner.firstname", { ns: "bike" })}
                                                    value={bike?.owner.ownerFirstname}
                                                />
                                            </>
                                        }
                                        <DataViewPDF
                                            label={t("owner.phonenumber", { ns: "bike" })}
                                            value={bike?.owner?.ownerPhonenumber?.replace("+33", "0")}
                                        />
                                        <DataViewPDF
                                            label={t("owner.mail", { ns: "bike" })}
                                            value={bike?.owner?.mail}
                                        />
                                    </>
                                    :
                                    <>
                                        <DataViewPDF
                                            label={t("owner.mail", { ns: "bike" })}
                                            value={bike?.bikeOwner?.mail}
                                        />
                                    </>
                                }
                            </View>
                        </View>
                    </View>
                }

                {children}
            </Page>
        </Document>
    )
}

export default PDFRequestTemplate
